import React, { useState, useRef } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { getOrganizationFromPath, getOrganizationRootPath, getOrganizationName, getOrganizationFolderInfo } from '../utilities/organizationUtils';

const Searchbar = ({ onSearch, onFileSelect, folderStructure }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentOrg = getOrganizationFromPath(location.pathname);
  
  // Generate context-aware placeholder
  const getSearchPlaceholder = () => {
    if (currentOrg) {
      const orgName = getOrganizationName(currentOrg);
      return `Søg i ${orgName}`;
    }
    return 'Søg i alle emner';
  };

  const handleSearch = async () => {
    if (!query) {
      onSearch([]); // Pass an empty array to clear the results
      return;
    }

    // Call Azure Function to perform the search
    try {
      let searchUrl = `${process.env.REACT_APP_SHAREPOINT_SEARCH_FUNCTION_URL}?searchTerm=${encodeURIComponent(query)}`;
      
      // Add organization filter if we're in organization context
      if (currentOrg && folderStructure) {
        const orgFolderInfo = getOrganizationFolderInfo(folderStructure, currentOrg);
        if (orgFolderInfo) {
          // Use just the clean folder name without any paths or slashes
          const folderName = orgFolderInfo.name;
          searchUrl += `&rootFolder=${encodeURIComponent(folderName)}`;
          console.log(`Searching within organization: ${currentOrg} (${folderName})`);
        } else {
          console.log(`Could not find folder info for organization: ${currentOrg}`);
        }
      } else {
        console.log('Searching across all organizations');
      }
      
      const response = await fetch(searchUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      onSearch(data); // Pass the search results to the parent component
    } catch (error) {
      console.error('Error fetching search results:', error);
      onSearch([]); // Pass an empty array to clear the results
    }
  };

  const handleSelect = (file) => {
    setQuery('');
    onFileSelect(file); // Pass the selected file to the parent component
    // Navigation is now handled in the parent's handleFileSelect function
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setQuery('');
    onSearch([]); // Clear search results
    inputRef.current?.focus(); // Keep focus on input
  };

  return (
    <div className="relative w-full md:w-80 lg:w-96">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={getSearchPlaceholder()}
        className="border px-4 py-2 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-400 pr-20"
      />
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-12 top-0 h-full px-2 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Clear search"
        >
          <FiX className="inline-block align-middle" />
        </button>
      )}
      <button
        onClick={handleSearch}
        className="absolute right-0 top-0 h-full px-4 py-2 text-white rounded-r-md hover:bg-orange-600 focus:outline-none"
        style={{ backgroundColor: 'rgb(238, 123, 25)' }}
      >
        <FiSearch className="inline-block align-middle" />
      </button>
    </div>
  );
};

export default Searchbar;